<template>
  <div class="crm-fiche card" v-if="lead">
    <div class="card-header d-flex justify-content-between align-items-start">
      <div>
        <h4 class="mb-1">
          {{ (lead.prenom || '') + ' ' + (lead.nom || '') }}
          <small class="text-muted">#{{ lead.pk_lead }}</small>
        </h4>
        <p class="mb-0 text-muted small">
          {{ lead.ville || '' }} {{ lead.cp || '' }}
          <span v-if="lead.date_collecte"> · Collecte {{ lead.date_collecte }}</span>
        </p>
        <span class="badge badge-pill mt-2" :class="'badge-' + statusMeta(crm.status).badge">
          {{ statusMeta(crm.status).label }}
        </span>
        <span
          v-if="showTarification && tarificationLabel(lead)"
          class="badge badge-pill mt-2 ml-1"
          :class="tarificationBadgeClass(lead)"
        >{{ tarificationLabel(lead) }}</span>
      </div>
      <button class="btn btn-link btn-sm text-danger" @click="$emit('close')">
        <i class="tim-icons icon-simple-remove"></i>
      </button>
    </div>

    <div class="card-body">
      <div class="btn-group flex-wrap mb-3">
        <a class="btn btn-sm btn-outline-market" :href="'tel:' + (lead.telephone || '')">Appeler</a>
        <button
          class="btn btn-sm btn-market"
          :disabled="(crm.calls_count || 0) >= maxCalls"
          @click="$emit('open-call')"
        >
          Logger un appel ({{ crm.calls_count || 0 }}/{{ maxCalls }})
        </button>
        <button class="btn btn-sm btn-default" @click="$emit('open-sms')">Modèle SMS</button>
        <button v-if="!full" class="btn btn-sm btn-link" @click="$emit('open-full')">Ouvrir la fiche</button>
      </div>

      <div class="row crm-meta-row mb-3" v-loading="stateSaving">
        <div class="col-md-4 mb-2 crm-meta-field">
          <label class="small text-muted text-uppercase crm-meta-label">Affectation</label>
          <div class="crm-meta-control">
            <el-select
              class="select-primary w-100"
              :value="crm.owner_commercial_id == null ? '' : String(crm.owner_commercial_id)"
              :disabled="stateSaving"
              @change="onCommercialChange"
              clearable
              placeholder="Non affecté"
            >
              <el-option value="" label="Non affecté" />
              <el-option
                v-for="c in commercials"
                :key="c.id"
                :value="String(c.id)"
                :label="(c.prenom || '') + ' ' + (c.nom || '')"
              />
            </el-select>
          </div>
        </div>
        <div class="col-md-4 mb-2 crm-meta-field">
          <label class="small text-muted text-uppercase crm-meta-label">Statut CRM</label>
          <div class="crm-meta-control">
            <el-select
              class="select-primary w-100"
              :value="displayStatus"
              :disabled="stateSaving"
              @change="onStatusChange"
            >
              <el-option
                v-for="s in statuses"
                :key="s.id"
                :value="s.id"
                :label="s.label"
              />
            </el-select>
          </div>
        </div>
        <div class="col-md-4 mb-2 crm-meta-field">
          <label class="small text-muted text-uppercase crm-meta-label">Rappel programmé</label>
          <div class="crm-meta-control">
            <el-date-picker
              :key="'cb-' + callbackPickerTick"
              class="w-100"
              type="datetime"
              value-format="yyyy-MM-dd HH:mm:ss"
              placeholder="Rappel"
              :value="callbackPickerValue"
              :disabled="stateSaving || callbackConfirmOpen"
              @input="onCallbackChange"
            />
          </div>
        </div>
      </div>

      <!-- Confirm rappel — même UI modal CRM que le reste de la PLF -->
      <modal
        :show.sync="callbackConfirmOpen"
        class="modal-crm"
        body-classes="p-0"
        :show-close="true"
        @close="cancelCallbackConfirm"
      >
        <card class="border-0 mb-0">
          <h4 slot="header" class="title title-up modal-crm-title">
            {{ callbackConfirmIsClear ? 'Supprimer le rappel' : 'Modifier le rappel' }}
          </h4>
          <div class="px-1 pb-1">
            <p v-if="callbackConfirmIsClear" class="mb-3">
              La date de rappel programmée va être <strong>supprimée</strong>.
            </p>
            <template v-else>
              <p class="mb-2 text-muted small text-uppercase">Date actuelle</p>
              <p class="mb-3">{{ callbackConfirmFromLabel }}</p>
              <p class="mb-2 text-muted small text-uppercase">Nouvelle date</p>
              <p class="mb-3">{{ callbackConfirmToLabel }}</p>
            </template>
            <p v-if="!callbackConfirmIsClear && callbackConfirmFromRaw" class="small text-muted mb-3">
              Confirmez pour appliquer cette modification.
            </p>
            <p v-else-if="!callbackConfirmIsClear" class="small text-muted mb-3">
              Confirmez pour programmer ce rappel.
            </p>
            <div class="text-right mt-3">
              <button type="button" class="btn btn-default" @click="cancelCallbackConfirm">
                Annuler
              </button>
              <button
                type="button"
                class="btn"
                :class="callbackConfirmIsClear ? 'btn-danger' : 'btn-market'"
                @click="confirmCallbackChange"
              >
                {{ callbackConfirmIsClear ? 'Supprimer' : 'Confirmer' }}
              </button>
            </div>
          </div>
        </card>
      </modal>

      <ul class="nav nav-pills nav-pills-primary mb-3">
        <li class="nav-item" v-for="t in tabs" :key="t.id">
          <a
            href="#"
            class="nav-link"
            :class="{ active: tab === t.id }"
            @click.prevent="tab = t.id"
          >{{ t.label }}</a>
        </li>
      </ul>

      <div v-show="tab === 'attrs'" class="crm-attrs">
        <div v-if="detailsLoading" class="crm-attrs-hint">Chargement des attributs…</div>
        <template v-else>
          <p v-if="mgUnavailable" class="small crm-attrs-hint mb-2">
            Détails MonGustave indisponibles
          </p>
          <p class="small crm-attrs-hint mb-2" v-if="fieldGroups.length">
            {{ filledCount }} champ(s) renseigné(s)
          </p>
          <div v-if="!fieldGroups.length" class="crm-attrs-hint small">Aucun attribut renseigné</div>
          <div v-for="g in fieldGroups" :key="g.title" class="mb-3 crm-attr-group">
            <h6 class="text-uppercase crm-attr-title mb-2" style="font-size: 11px; letter-spacing: 0.04em;">
              {{ g.title }}
              <span class="badge badge-secondary ml-1">{{ g.rows.length }}</span>
            </h6>
            <table class="table table-sm table-borderless mb-0 crm-attr-table">
              <tr v-for="(r, idx) in g.rows" :key="idx">
                <td class="crm-attr-label UpperCase" style="width: 42%;">{{ r.label }}</td>
                <td class="crm-attr-value UpperCase">
                  {{ r.value }}
                  <button
                    type="button"
                    class="btn btn-link btn-sm p-0 ml-1"
                    title="Copier"
                    @click="copyVal(r.value)"
                  >
                    <i class="tim-icons icon-single-copy-04" style="font-size: 12px;"></i>
                  </button>
                </td>
              </tr>
            </table>
          </div>
        </template>
      </div>

      <div v-show="tab === 'comments'">
        <comments-block
          :comments="crm.comments || []"
          :draft="commentDraftLocal"
          :saving="commentSaving"
          @update:draft="onDraft"
          @add="$emit('add-comment', commentDraftLocal)"
        />
        <div class="mt-3" v-if="isAutoLead">
          <pieces-block :crm="crm" :disabled="stateSaving" @toggle="onPieceToggle" />
        </div>
      </div>

      <div v-show="tab === 'consent'">
        <p><strong>Phrase d'optin :</strong> {{ consentPhrase || '—' }}</p>
        <p>
          <strong>Site :</strong>
          <a
            v-if="consentSiteLink"
            :href="consentSiteLink"
            target="_blank"
            rel="noopener noreferrer"
          >{{ consentSite }}</a>
          <template v-else>{{ consentSite || '—' }}</template>
        </p>
        <p><strong>Date collecte :</strong> {{ lead.date_collecte || '—' }}</p>
        <p>
          <strong>Facturation :</strong>
          <span class="badge" :class="lead.facturation === 'facturable' ? 'badge-success' : 'badge-secondary'">
            {{ (lead.facturation || '').toUpperCase() || '—' }}
          </span>
        </p>
      </div>

      <div v-show="tab === 'histo'">
        <h6 class="text-uppercase text-muted" style="font-size: 11px;">Appels</h6>
        <p v-if="!(crm.calls && crm.calls.length)" class="text-muted small">Aucun appel</p>
        <ul v-else class="list-unstyled">
          <li v-for="(c, i) in callsSorted" :key="c.id || ('call-' + i)" class="mb-2">
            <span class="text-muted small">{{ c.called_at || c.at || '—' }} — {{ c.created_by || c.by || '—' }}</span><br />
            {{ c.result || '—' }}
          </li>
        </ul>
        <h6 class="text-uppercase text-muted mt-3" style="font-size: 11px;">SMS</h6>
        <p v-if="!(crm.sms && crm.sms.length)" class="text-muted small">Aucun SMS</p>
        <ul v-else class="list-unstyled">
          <li v-for="(s, i) in smsSorted" :key="s.id || ('sms-' + i)" class="mb-2">
            <span class="text-muted small">{{ s.created_at || '—' }} — {{ s.created_by || '—' }}</span><br />
            {{ s.body }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { Select, Option, DatePicker } from 'element-ui'
import { Modal, Card } from 'src/components'
import CommentsBlock from './CommentsBlock.vue'
import PiecesBlock from './PiecesBlock.vue'
import {
  CRM_STATUSES,
  MAX_CALLS,
  statusMeta,
  countFilledAttrs,
  isAutoProduct,
  prettyCallbackAt,
  isFilledValue,
  tarificationLabel,
  tarificationBadgeClass
} from './crmStatuses'

export default {
  name: 'ContactFiche',
  components: {
    CommentsBlock,
    PiecesBlock,
    Modal,
    Card,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker
  },
  props: {
    lead: { type: Object, required: true },
    crm: { type: Object, default: () => ({}) },
    commercials: { type: Array, default: () => [] },
    fieldGroups: { type: Array, default: () => [] },
    detailsLoading: { type: Boolean, default: false },
    mgUnavailable: { type: Boolean, default: false },
    mgConsent: { type: Object, default: null },
    stateSaving: { type: Boolean, default: false },
    full: { type: Boolean, default: false },
    commentDraft: { type: String, default: '' },
    commentSaving: { type: Boolean, default: false },
    showTarification: { type: Boolean, default: false }
  },
  data() {
    return {
      tab: 'attrs',
      statuses: CRM_STATUSES,
      maxCalls: MAX_CALLS,
      commentDraftLocal: this.commentDraft,
      pendingStatus: null,
      callbackConfirmOpen: false,
      callbackPendingValue: undefined,
      callbackPickerTick: 0
    }
  },
  computed: {
    isAutoLead() {
      return isAutoProduct(this.lead, [])
    },
    tabs() {
      return [
        { id: 'attrs', label: 'Attributs' },
        { id: 'comments', label: this.isAutoLead ? 'Notes & pièces' : 'Notes' },
        { id: 'consent', label: 'Consentement' },
        { id: 'histo', label: 'Appels & SMS' }
      ]
    },
    filledCount() {
      return countFilledAttrs(this.fieldGroups)
    },
    consentPhrase() {
      const mg = this.mgConsent && this.mgConsent.phrase_optin
      if (mg != null && String(mg).trim() !== '') return mg
      return (this.lead && this.lead.phrase_optin) || ''
    },
    consentSite() {
      const mg = this.mgConsent && this.mgConsent.site_collecte
      if (mg != null && String(mg).trim() !== '') return mg
      return (this.lead && this.lead.site_collecte) || ''
    },
    consentSiteLink() {
      const s = String(this.consentSite || '').trim()
      if (!s) return ''
      if (/^https?:\/\//i.test(s)) return s
      if (/^[a-z0-9.-]+\.[a-z]{2,}/i.test(s)) return 'https://' + s
      return ''
    },
    displayStatus() {
      return this.pendingStatus != null ? this.pendingStatus : (this.crm && this.crm.status)
    },
    callsSorted() {
      return [...((this.crm && this.crm.calls) || [])].reverse()
    },
    smsSorted() {
      return [...((this.crm && this.crm.sms) || [])].reverse()
    },
    callbackPickerValue() {
      // force remount key via tick when cancelling so picker snaps back
      void this.callbackPickerTick
      return (this.crm && this.crm.callback_at) || null
    },
    callbackConfirmFromRaw() {
      return (this.crm && this.crm.callback_at) || null
    },
    callbackConfirmIsClear() {
      return !isFilledValue(this.callbackPendingValue)
    },
    callbackConfirmFromLabel() {
      return isFilledValue(this.callbackConfirmFromRaw)
        ? prettyCallbackAt(this.callbackConfirmFromRaw)
        : 'Aucun rappel'
    },
    callbackConfirmToLabel() {
      return isFilledValue(this.callbackPendingValue)
        ? prettyCallbackAt(this.callbackPendingValue)
        : '—'
    }
  },
  watch: {
    commentDraft(v) {
      this.commentDraftLocal = v
    },
    lead() {
      // Only reset tab when switching to another contact, not on same pk re-bind
      if (!this._lastLeadPk || String(this._lastLeadPk) !== String(this.lead && this.lead.pk_lead)) {
        this.tab = 'attrs'
        this.pendingStatus = null
        this.cancelCallbackConfirm(false)
      }
      this._lastLeadPk = this.lead && this.lead.pk_lead
    },
    'crm.status'(v) {
      // Clear local pending once parent/API status catches up
      if (this.pendingStatus != null && v === this.pendingStatus) {
        this.pendingStatus = null
      }
    },
    stateSaving(v) {
      if (!v && this.pendingStatus != null && this.crm && this.crm.status === this.pendingStatus) {
        this.pendingStatus = null
      }
      if (!v && this.pendingStatus != null && this.crm && this.crm.status !== this.pendingStatus) {
        // Save finished but parent kept/rolled back another status — drop pending
        this.pendingStatus = null
      }
    }
  },
  methods: {
    statusMeta,
    tarificationLabel,
    tarificationBadgeClass,
    onDraft(v) {
      this.commentDraftLocal = v
      this.$emit('update:commentDraft', v)
    },
    onStatusChange(status) {
      this.pendingStatus = status
      this.$emit('update-state', { status })
    },
    normalizeCallback(v) {
      if (!isFilledValue(v)) return null
      return String(v).trim()
    },
    onCallbackChange(callback_at) {
      const next = this.normalizeCallback(callback_at)
      const prev = this.normalizeCallback(this.crm && this.crm.callback_at)
      if (next === prev) {
        this.callbackPickerTick += 1
        return
      }
      this.callbackPendingValue = next
      this.callbackConfirmOpen = true
      // Remettre le picker sur la valeur actuelle tant que non confirmé
      this.$nextTick(() => {
        this.callbackPickerTick += 1
      })
    },
    confirmCallbackChange() {
      const next = this.normalizeCallback(this.callbackPendingValue)
      this.callbackConfirmOpen = false
      this.callbackPendingValue = undefined
      this.$emit('update-state', { callback_at: next })
    },
    cancelCallbackConfirm(bump = true) {
      this.callbackConfirmOpen = false
      this.callbackPendingValue = undefined
      if (bump !== false) this.callbackPickerTick += 1
    },
    onCommercialChange(val) {
      if (!val) {
        this.$emit('update-state', { clear_commercial: true, owner_commercial_id: null })
      } else {
        this.$emit('update-state', { owner_commercial_id: val, clear_commercial: false })
      }
    },
    onPieceToggle(key, checked) {
      this.$emit('update-state', { [key]: !!checked })
    },
    copyVal(v) {
      const text = String(v || '')
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.$notify({ type: 'success', message: 'Copié' })
        }).catch(() => this.fallbackCopy(text))
      } else {
        this.fallbackCopy(text)
      }
    },
    fallbackCopy(text) {
      const area = document.createElement('textarea')
      area.value = text
      document.body.appendChild(area)
      area.select()
      document.execCommand('copy')
      area.remove()
      this.$notify({ type: 'success', message: 'Copié' })
    }
  }
}
</script>

<style scoped>
.btn-outline-market {
  border: 1px solid #588bbd;
  color: #588bbd;
}
.w-100 {
  width: 100%;
}
.UpperCase {
  text-transform: uppercase;
}
.crm-attrs-hint,
.crm-attr-title {
  color: rgba(255, 255, 255, 0.85) !important;
}
.crm-attr-group {
  border: 1px solid rgba(88, 139, 189, 0.35);
  border-radius: 6px;
  padding: 8px 10px;
}
.crm-attr-table >>> tbody > tr > td,
.crm-attr-table >>> tr > td,
.crm-attr-label,
.crm-attr-value {
  color: #fff !important;
  opacity: 1;
}
.crm-attr-label {
  color: rgba(255, 255, 255, 0.9) !important;
  font-weight: 500;
}
.crm-attr-value {
  color: #fff !important;
  font-weight: 600;
}
.crm-attr-value .btn-link {
  color: #588bbd !important;
}
.crm-meta-field {
  display: flex;
  flex-direction: column;
}
.crm-meta-label {
  display: flex;
  align-items: flex-end;
  min-height: 2.5em;
  margin-bottom: 6px;
  line-height: 1.25;
}
.crm-meta-control {
  width: 100%;
  margin-top: auto;
}
.crm-meta-control >>> .el-select,
.crm-meta-control >>> .el-date-editor {
  width: 100% !important;
}
.crm-meta-control >>> .el-input__inner {
  height: 36px;
  line-height: 36px;
}
</style>
